/**
 * Subsystem registry with dependency ordering. ARCHITECTURE.md §3.
 */

/**
 * Base class. Extending it is optional — the engine only requires the documented shape —
 * but it documents the contract and gives every system a disposal bag.
 */
export class System {
  static id = 'unnamed';
  static deps = [];
  static budgetMs = 0;

  async init(_ctx) {}
  fixedUpdate(_step, _ctx) {}
  update(_dt, _ctx) {}
  lateUpdate(_dt, _ctx) {}
  resize(_width, _height, _ctx) {}
  dispose() {}
}

export class SystemRegistry {
  constructor() {
    /** @type {Map<string, object>} */
    this._byId = new Map();
    /** @type {Array<object>} instances in topological order */
    this.ordered = [];
    /** @type {Array<{id:string, deps:string[], Class:Function}>} */
    this._pending = [];
    this._sealed = false;
  }

  add(SystemClass) {
    const id = SystemClass.id;
    if (!id || id === 'unnamed') {
      throw new Error(`System ${SystemClass.name} must declare a static id.`);
    }
    if (this._byId.has(id) || this._pending.some((p) => p.id === id)) {
      throw new Error(`Duplicate system id "${id}".`);
    }
    if (this._sealed) throw new Error(`Cannot register "${id}" after init().`);
    this._pending.push({ id, deps: SystemClass.deps || [], Class: SystemClass });
    return this;
  }

  get(id) {
    return this._byId.get(id) || null;
  }

  has(id) {
    return this._byId.has(id);
  }

  /** Instantiates every registered class in dependency order. */
  build() {
    const order = topoSort(this._pending);
    for (const entry of order) {
      const instance = new entry.Class();
      instance.constructor.id = entry.id; // keep id reachable from the instance
      this._byId.set(entry.id, instance);
      this.ordered.push(instance);
    }
    this._sealed = true;
    this._pending.length = 0;
    return this.ordered;
  }

  forEach(fn) {
    for (let i = 0; i < this.ordered.length; i++) fn(this.ordered[i]);
  }

  ids() {
    return this.ordered.map((s) => s.constructor.id);
  }

  clear() {
    this._byId.clear();
    this.ordered.length = 0;
    this._pending.length = 0;
    this._sealed = false;
  }
}

/**
 * Kahn topological sort with a deterministic tie-break (registration order), so the
 * final ordering never depends on Map iteration quirks or registration timing races.
 * @param {Array<{id:string, deps:string[]}>} entries
 */
export function topoSort(entries) {
  const byId = new Map();
  entries.forEach((e, i) => byId.set(e.id, { ...e, seq: i, indeg: 0 }));

  for (const e of byId.values()) {
    for (const d of e.deps) {
      if (!byId.has(d)) {
        throw new Error(`System "${e.id}" depends on unregistered system "${d}".`);
      }
      e.indeg++;
    }
  }

  // Ready set kept sorted by registration order for determinism.
  const ready = [...byId.values()].filter((e) => e.indeg === 0).sort((a, b) => a.seq - b.seq);
  const out = [];
  const dependents = new Map();
  for (const e of byId.values()) {
    for (const d of e.deps) {
      if (!dependents.has(d)) dependents.set(d, []);
      dependents.get(d).push(e.id);
    }
  }

  while (ready.length > 0) {
    const node = ready.shift();
    out.push(node);
    const deps = dependents.get(node.id);
    if (!deps) continue;
    for (const depId of deps) {
      const dep = byId.get(depId);
      dep.indeg--;
      if (dep.indeg === 0) {
        // Insert keeping the ready list sorted by registration order.
        let i = 0;
        while (i < ready.length && ready[i].seq < dep.seq) i++;
        ready.splice(i, 0, dep);
      }
    }
  }

  if (out.length !== byId.size) {
    const stuck = [...byId.values()].filter((e) => !out.includes(e)).map((e) => e.id);
    throw new Error(`Dependency cycle among systems: ${stuck.join(' -> ')}`);
  }
  return out;
}
